import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  TextField,
  MenuItem,
  Button,
  Box,
  Table,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Paper,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import SuperAdminLayout from '../../../layouts/SuperAdminLayout';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from './../../../utils/api';

const CreatePChallan = () => {
  const user = JSON.parse(localStorage.getItem('user'));
  const superAdminId = user?.id;
  const navigate = useNavigate();

  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [units, setUnits] = useState([]);
  const [form, setForm] = useState({
    customer: '',
    challanNumber: '',
    date: new Date().toISOString().split('T')[0],
    items: [
      {
        product: '',
        unit: '',
        quantity: 0,
        price: 0,
      },
    ],
  });

  const [openCustomerDialog, setOpenCustomerDialog] = useState(false);
  const [openProductDialog, setOpenProductDialog] = useState(false);

  const [newCustomer, setNewCustomer] = useState({
    partyName: '',
    partyMarka: '',
    address: '',
    transporterName: '',
    transporterAddress: '',
  });

  const [newProduct, setNewProduct] = useState({
    productName: '',
    openingQuantity: '',
    purchaseRate: '',
    primaryUnit: '',
    secondaryUnit: '',
    secondaryQuantity: '',
  });

  const [openUnitDialog, setOpenUnitDialog] = useState(false);
  const [newUnit, setNewUnit] = useState('');

  const getTotal = () =>
    form.items.reduce((sum, item) => sum + item.quantity * item.price, 0);

  useEffect(() => {
    const fetchData = async () => {
      const [custRes, prodRes, challanRes, unitRes] = await Promise.all([
        api.get(`/api/customers/get-all/${superAdminId}`),
        api.get(`/api/products/get-all/${superAdminId}`),
        api.get(`/api/pchallans/count/${superAdminId}`),
        api.get(`/api/units/get-all/${superAdminId}`),
      ]);

      setCustomers(custRes.data);
      setProducts(prodRes.data);
      setUnits(unitRes.data);

      const count = challanRes.data.count ?? 0;
      setForm((prev) => ({
        ...prev,
        challanNumber: String(count + 1).padStart(3, '0'),
      }));
    };

    fetchData();
  }, [superAdminId]);

  useEffect(() => {
    const fetchProductsWithStock = async () => {
      try {
        // 1. Fetch base product list
        const productRes = await api.get(
          `/api/products/get-all/${superAdminId}`
        );

        // 2. Fetch closing stock report
        const stockRes = await api.get(
          `/api/reports/closing-stock/${superAdminId}`
        );

        // 3. Merge closingQty from stock into products
        const stockMap = {};
        stockRes.data.forEach((item) => {
          stockMap[item.productName] = item.closingQty;
        });

        const combinedProducts = productRes.data.map((product) => ({
          ...product,
          closingQty: stockMap[product.productName] || 0,
        }));

        setProducts(combinedProducts);
      } catch (err) {
        console.error('Failed to fetch products/stock:', err);
      }
    };

    if (superAdminId) {
      fetchProductsWithStock();
    }
  }, [superAdminId]);

  const handleItemChange = (index, key, value) => {
    const updatedItems = [...form.items];
    const item = updatedItems[index];

    // If product details aren't available, skip calculations
    const secQtyFromProduct = item?.productDetails?.secondaryQuantity || 1;

    if (key === 'quantity') {
      item.quantity = value;
      item.secondaryQuantity = parseFloat(
        (value / secQtyFromProduct).toFixed(2)
      );
    } else if (key === 'secondaryQuantity') {
      item.secondaryQuantity = value;
      item.quantity = parseFloat((value * secQtyFromProduct).toFixed(2));
    } else {
      item[key] = value;
    }

    // If product is selected, fetch and store unit & closing stock
    if (key === 'product') {
      const selectedProduct = products.find((p) => p.productName === value);
      if (selectedProduct) {
        item.unit = selectedProduct.primaryUnit;
        item.productDetails = selectedProduct;
        item.availableQty = selectedProduct.closingQty || 0;
      }
    }

    updatedItems[index] = item;
    setForm({ ...form, items: updatedItems });
  };

  const addRow = () => {
    setForm((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          product: '',
          unit: '',
          availableQty: '',
          secondaryQuantity: '',
          quantity: 0,
          price: 0,
        },
      ],
    }));
  };

  const removeRow = (index) => {
    const updated = form.items.filter((_, i) => i !== index);
    setForm({ ...form, items: updated });
  };

  const handleSubmit = async () => {
    for (const item of form.items) {
      const product = products.find((p) => p.productName === item.product);
      if (!product || !product.minSalePrice) {
        return alert(
          `❌ Min Sale Price not defined for product: ${item.product}`
        );
      }
    }
    try {
      const payload = {
        ...form,
        superAdminId,
        total: getTotal(),
      };
      await api.post(`/api/pchallans/create`, payload);
      alert('Challan created successfully!');
      navigate('/super-admin/pchallan/view');
    } catch (err) {
      console.error(err);
      alert('Failed to create challan');
    }
  };

  const fetchUnits = async () => {
    try {
      const res = await api.get(`/api/units/get-all/${superAdminId}`);
      setUnits(res.data);
    } catch (err) {
      console.error('Failed to fetch units:', err);
    }
  };

  return (
    <SuperAdminLayout>
      <Dialog
        open={openCustomerDialog}
        onClose={() => setOpenCustomerDialog(false)}
      >
        <DialogTitle>Create Customer</DialogTitle>
        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
        >
          <TextField
            label="Party Name"
            value={newCustomer.partyName}
            onChange={(e) =>
              setNewCustomer({ ...newCustomer, partyName: e.target.value })
            }
          />
          <TextField
            label="Party Marka"
            value={newCustomer.partyMarka}
            onChange={(e) =>
              setNewCustomer({ ...newCustomer, partyMarka: e.target.value })
            }
          />
          <TextField
            label="Address"
            value={newCustomer.address}
            onChange={(e) =>
              setNewCustomer({ ...newCustomer, address: e.target.value })
            }
          />
          <TextField
            label="Transporter Name"
            value={newCustomer.transporterName}
            onChange={(e) =>
              setNewCustomer({
                ...newCustomer,
                transporterName: e.target.value,
              })
            }
          />
          <TextField
            label="Transporter Address"
            value={newCustomer.transporterAddress}
            onChange={(e) =>
              setNewCustomer({
                ...newCustomer,
                transporterAddress: e.target.value,
              })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCustomerDialog(false)}>Cancel</Button>
          <Button
            onClick={async () => {
              await api.post(`/api/customers/create`, {
                ...newCustomer,
                superAdminId,
              });
              setOpenCustomerDialog(false);
              const res = await api.get(
                `/api/customers/get-all/${superAdminId}`
              );
              setCustomers(res.data);
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openUnitDialog} onClose={() => setOpenUnitDialog(false)}>
        <DialogTitle>Create Unit</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Unit Name"
            value={newUnit}
            onChange={(e) => setNewUnit(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenUnitDialog(false)}>Cancel</Button>
          <Button
            onClick={async () => {
              if (!newUnit.trim()) return;
              try {
                await api.post(`/api/units/create`, {
                  name: newUnit,
                  superAdminId,
                });
                setNewUnit('');
                setOpenUnitDialog(false);
                await fetchUnits(); // ⬅ refresh unit list
              } catch (err) {
                console.error('Failed to create unit:', err);
              }
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openProductDialog}
        onClose={() => setOpenProductDialog(false)}
      >
        <DialogTitle>Create Product</DialogTitle>
        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
        >
          <TextField
            label="Product Name"
            value={newProduct.productName}
            onChange={(e) =>
              setNewProduct({ ...newProduct, productName: e.target.value })
            }
          />
          <TextField
            label="Opening Quantity"
            type="number"
            value={newProduct.openingQuantity}
            onChange={(e) =>
              setNewProduct({ ...newProduct, openingQuantity: e.target.value })
            }
          />
          <TextField
            label="Rate"
            type="number"
            value={newProduct.rate}
            onChange={(e) =>
              setNewProduct({ ...newProduct, purchaseRate: e.target.value })
            }
          />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="body2" onClick={() => setOpenUnitDialog(true)}>
              Create Unit
            </Typography>
          </Box>
          <TextField
            select
            label="Primary Unit"
            value={newProduct.primaryUnit}
            onChange={(e) =>
              setNewProduct({ ...newProduct, primaryUnit: e.target.value })
            }
          >
            {units.map((unit) => (
              <MenuItem key={unit._id} value={unit.name}>
                {unit.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Secondary Unit"
            value={newProduct.secondaryUnit}
            onChange={(e) =>
              setNewProduct({ ...newProduct, secondaryUnit: e.target.value })
            }
          >
            {units.map((unit) => (
              <MenuItem key={unit._id} value={unit.name}>
                {unit.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Secondary Quantity"
            type="number"
            value={newProduct.secondaryQuantity}
            onChange={(e) =>
              setNewProduct({
                ...newProduct,
                secondaryQuantity: e.target.value,
              })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenProductDialog(false)}>Cancel</Button>
          <Button
            onClick={async () => {
              await api.post(`/api/products/create`, {
                ...newProduct,
                superAdminId,
              });
              setOpenProductDialog(false);
              const res = await api.get(
                `/api/products/get-all/${superAdminId}`
              );
              setProducts(res.data);
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Container
        maxWidth="lg"
        sx={{
          mt: 4,
          mb: 4,
          background: '#fff',
          p: 4,
          borderRadius: 2,
          boxShadow: 3,
        }}
      >
        <Typography variant="h5" align="center" gutterBottom>
          Create Sales Challan
        </Typography>

        <Typography
          onClick={() => setOpenCustomerDialog(true)}
          sx={{ cursor: 'pointer' }}
        >
          Create Customer
        </Typography>
        {/* Header Fields */}
        <Box display="flex" gap={2} mb={3}>
          <TextField
            select
            label="Customer"
            fullWidth
            value={form.customer}
            onChange={(e) => setForm({ ...form, customer: e.target.value })}
          >
            {customers.map((c) => (
              <MenuItem key={c._id} value={c.partyName}>
                {c.partyName}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Challan No"
            fullWidth
            value={form.challanNumber}
            onChange={(e) =>
              setForm({ ...form, challanNumber: e.target.value })
            }
          />

          <TextField
            type="date"
            label="Date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
          />
        </Box>

        {/* Items Table */}
        <Paper elevation={2} sx={{ mb: 3 }}>
          <Table>
            <TableBody>
              <Typography
                sx={{ ml: 2, mt: 2 }}
                onClick={() => {
                  setOpenProductDialog(true);
                  fetchUnits();
                }}
              >
                Create Product
              </Typography>
              {form.items.map((item, idx) => (
                <TableRow key={idx}>
                  <TableCell>
                    <TextField
                      select
                      fullWidth
                      label={'Product Name'}
                      value={item.product}
                      sx={{ width: 240 }}
                      onChange={(e) =>
                        handleItemChange(idx, 'product', e.target.value)
                      }
                      size="small"
                    >
                      {products.map((p) => (
                        <MenuItem key={p._id} value={p.productName}>
                          {p.productName}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>

                  <TableCell>
                    <TextField
                      select
                      fullWidth
                      label={'Unit'}
                      value={item.unit}
                      sx={{ width: 80 }}
                      onChange={(e) =>
                        handleItemChange(idx, 'unit', e.target.value)
                      }
                      size="small"
                    >
                      {units.map((u) => (
                        <MenuItem key={u._id} value={u.name}>
                          {u.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>

                  <TableCell>
                    <TextField
                      type="number"
                      fullWidth
                      label={'Aval Stock'}
                      value={item.availableQty}
                      InputProps={{ readOnly: true }}
                      size="small"
                    />
                  </TableCell>

                  <TableCell>
                    <TextField
                      type="number"
                      fullWidth
                      label={'Secondary Qty'}
                      value={item.secondaryQuantity}
                      onChange={(e) =>
                        handleItemChange(
                          idx,
                          'secondaryQuantity',
                          Number(e.target.value)
                        )
                      }
                      size="small"
                    />
                  </TableCell>

                  <TableCell>
                    <TextField
                      type="number"
                      fullWidth
                      label={'Primary Qty'}
                      value={item.quantity}
                      onChange={(e) =>
                        handleItemChange(
                          idx,
                          'quantity',
                          Number(e.target.value)
                        )
                      }
                      size="small"
                    />
                  </TableCell>

                  <TableCell>
                    <TextField
                      type="number"
                      fullWidth
                      label={'Price'}
                      value={item.price}
                      onChange={(e) =>
                        handleItemChange(idx, 'price', Number(e.target.value))
                      }
                      size="small"
                    />
                  </TableCell>

                  <TableCell>
                    ₹{(item.quantity * item.price).toFixed(2)}
                  </TableCell>

                  <TableCell>
                    <IconButton onClick={() => removeRow(idx)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>

        {/* Controls */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Button variant="outlined" onClick={addRow}>
            + Add Row
          </Button>

          <Typography variant="h6">Total: ₹{getTotal().toFixed(2)}</Typography>
        </Box>

        <Box textAlign="center" mt={4}>
          <Button variant="contained" color="primary" onClick={handleSubmit}>
            Save Challan
          </Button>
        </Box>
      </Container>
    </SuperAdminLayout>
  );
};

export default CreatePChallan;
